import type { ChartDataPoint } from '../../types/ChartDataPoint';
import type { ReferenceLineMetadata } from '../types/ChartContext';
import type { XDataKey } from '../types/XDataKey';

import type { SnapStrategy, SnapStrategyContext } from './tooltipSnap';
import { filterDataByZoomDomain, type XScaleType, type ZoomDomain } from './zoomDomainFilter';

/**
 * Result type for finding the closest snap target (data point or reference line).
 */
export interface ClosestSnapTarget {
  /** X value to display in the tooltip (number for time/linear, string for categories) */
  xValue: number | string;
  /** X position in SVG coordinates to snap to */
  xPosition: number;
  /** Distance from cursor to the snap target */
  distance: number;
  /** Whether we snapped to a data point (true) or just a reference line (false) */
  hasDataPoint: boolean;
  /** Reference lines at or near this position that should show in the tooltip */
  nearbyReferenceLines: ReferenceLineMetadata[];
}

/**
 * Helper to check if a reference line has tooltip content.
 */
function hasTooltipContent(m: ReferenceLineMetadata): boolean {
  return Boolean(m.tooltipName || (m.events && m.events.length > 0));
}

/**
 * Gathers reference lines at an exact point value.
 * Used when snapping to a reference line (not a data point).
 * Supports both numeric points (timestamps) and string points (categories).
 */
function gatherRefLinesAtPoint(
  point: number | string,
  referenceLines: Map<string, ReferenceLineMetadata>,
): ReferenceLineMetadata[] {
  const result: ReferenceLineMetadata[] = [];
  for (const metadata of referenceLines.values()) {
    if (metadata.type !== 'vertical' || !hasTooltipContent(metadata)) {
      continue;
    }
    if (metadata.point === point) {
      result.push(metadata);
    }
  }
  return result;
}

/**
 * Finds the closest snap target (data point or reference line) based on cursor position.
 * Uses the provided strategy for chart-type-specific snapping behavior.
 *
 * @param cursorSvgX - Current cursor X position in SVG coordinates
 * @param chartData - Array of data points from the chart
 * @param referenceLines - Map of reference line metadata from ChartContext
 * @param xDataKey - The key to access X values in data objects
 * @param xScale - The scale function to convert data values to SVG coordinates
 * @param snapDistance - Maximum distance (in pixels) to consider a "snap"
 * @param strategy - The snap strategy for chart-type-specific behavior
 * @param xScaleType - Optional scale type for zoom support
 * @param zoomDomain - Optional zoom domain for zoom support
 */
export function findClosestRefLineOrDataPoint(
  cursorSvgX: number,
  chartData: ChartDataPoint[],
  referenceLines: Map<string, ReferenceLineMetadata>,
  xDataKey: XDataKey,
  xScale: (value: number) => number | undefined,
  snapDistance: number,
  strategy: SnapStrategy,
  xScaleType?: XScaleType,
  zoomDomain?: ZoomDomain,
): ClosestSnapTarget | null {
  // Use centralized zoom filtering utility
  const filteredChartDataByZoomDomain = filterDataByZoomDomain(
    chartData,
    zoomDomain ?? null,
    xScaleType ?? 'time',
    xDataKey,
  );

  // Build strategy context
  const context: SnapStrategyContext = {
    cursorSvgX,
    chartData: filteredChartDataByZoomDomain,
    xDataKey,
    xScale,
    snapDistance,
    xScaleType,
    zoomDomain,
  };

  // Use strategy to find closest targets
  const closestDataPoint = strategy.findClosestDataPoint(context);
  const closestRefLine = strategy.findClosestReferenceLine(context, referenceLines);

  if (!closestDataPoint && !closestRefLine) {
    return null;
  }

  const dataPointDistance = closestDataPoint?.distance ?? Infinity;
  const refLineDistance = closestRefLine?.distance ?? Infinity;

  // Check if ref line and data point are colocated (within 1px)
  const colocated =
    closestRefLine && closestDataPoint && Math.abs(closestRefLine.xPosition - closestDataPoint.xPosition) <= 1;

  // Snap to data point if it's closer OR if they're colocated
  if (closestDataPoint && (dataPointDistance <= refLineDistance || colocated)) {
    return {
      xValue: closestDataPoint.xValue,
      xPosition: closestDataPoint.xPosition,
      distance: closestDataPoint.distance,
      hasDataPoint: true,
      nearbyReferenceLines: strategy.gatherNearbyReferenceLines(closestDataPoint.xPosition, referenceLines, context),
    };
  }

  // Snap to reference line
  if (closestRefLine) {
    return {
      xValue: closestRefLine.point,
      xPosition: closestRefLine.xPosition,
      distance: closestRefLine.distance,
      hasDataPoint: false,
      nearbyReferenceLines: gatherRefLinesAtPoint(closestRefLine.point, referenceLines),
    };
  }

  return null;
}
